import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import {
  BasicTableSettings,
  RefreshableTableSettings,
  createPersistedStore,
  refreshableSettings,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import {
  TableSettingsMenu,
  TableSettingsMenuAutoRefresh,
  Datatable,
} from '@@/datatables';
import { Modal } from '@@/modals';

import { useActiveAlerts } from './useActiveAlerts';
import { createColumns } from './columns';
import { transformToActiveAlertRows } from './utils';
import { ActiveAlertRow } from './types';
import { SilenceAlertForm } from './SilenceAlertForm';

const storageKey = 'portainer.active.alerts';
interface TableSettings extends BasicTableSettings, RefreshableTableSettings {}
const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  { id: 'startsAt', desc: true },
  (set) => ({
    ...refreshableSettings(set, 10 * 1000),
  })
);

export function ActiveAlertsDatatable() {
  const tableState = useTableState(settingsStore, storageKey);
  const [alertToSilence, setAlertToSilence] = useState<ActiveAlertRow | null>(
    null
  );

  const alertsQuery = useActiveAlerts({
    refetchInterval: tableState.autoRefreshRate
      ? tableState.autoRefreshRate * 1000
      : undefined,
    select: transformToActiveAlertRows,
  });

  function handleSilence(alert: ActiveAlertRow) {
    setAlertToSilence(alert);
  }

  function handleSilenceCancel() {
    setAlertToSilence(null);
  }

  function handleSilenceSave() {
    // TODO: Implement silence save logic
    setAlertToSilence(null);
    // Optionally refresh the alerts query
    alertsQuery.refetch();
  }

  const columns = createColumns(handleSilence);

  return (
    <>
      <Datatable<ActiveAlertRow>
        dataset={alertsQuery.data || []}
        columns={columns}
        isLoading={alertsQuery.isLoading}
        settingsManager={tableState}
        emptyContentLabel="No active alerts found"
        title="Active Alerts"
        disableSelect
        titleIcon={AlertTriangle}
        renderTableSettings={() => (
          <TableSettingsMenu>
            <TableSettingsMenuAutoRefresh
              onChange={tableState.setAutoRefreshRate}
              value={tableState.autoRefreshRate}
            />
          </TableSettingsMenu>
        )}
        data-cy="active-alerts-datatable"
      />

      {alertToSilence && (
        <Modal onDismiss={handleSilenceCancel} size="lg">
          <SilenceAlertForm
            alert={alertToSilence}
            onSave={handleSilenceSave}
            onCancel={handleSilenceCancel}
          />
        </Modal>
      )}
    </>
  );
}
